import { createJSONStorage, persist } from 'zustand/middleware'
import type { StateStorage } from 'zustand/middleware/persist'
import { create } from 'zustand'
import { localStorageKeys } from 'lib/consts/browserStorage'

interface Store {
  businessId: string | null
  setBusinessId: (businessId: string | null) => void
}

/**
 * This store holds the current business ID
 * There is one more LS key that stores business ID but since it resets on different pages,
 * we use this store to keep the business ID in memory.
 */
const businessIdStore = create(
  persist<Store>(
    (set) => ({
      businessId: null,
      setBusinessId: (businessId) => set({ businessId }),
    }),
    {
      name: localStorageKeys.businessIdForBusinessStorage,
    },
  ),
)

// Global set to keep reInitialization functions for all business-aware stores.
const businessStores = new Set<{ reInit: () => void }>()

/**
 * Initializes the business storage with the given business ID.
 * If the business ID is already set, it does nothing.
 * It also re-initializes all registered business stores to ensure they are aware of the new business ID.
 * initBusinessStorage is populated with businessId exactly the same way as useBusinessIdPathParam so it should be consistent with it.
 */
const initBusinessStorage = (businessId: string | null) => {
  if (businessIdStore.getState().businessId === businessId) {
    return
  }
  businessIdStore.getState().setBusinessId(businessId)

  businessStores.forEach((store) => {
    store.reInit()
  })
}

/**
 * Generates a storage key by prefixing the base key with the current business ID.
 * If the business ID is not set it returns the base key.
 */
const getStorageKey = (baseKey: string) => {
  const businessId = businessIdStore.getState().businessId

  if (!businessId) {
    return baseKey
  }
  return `${businessId}-${baseKey}`
}

/**
 * Creates a scoped JSON storage for business-aware Zustand stores.
 * This storage prefixes all keys with the current business ID.
 * This is used to ensure that different businesses do not interfere with each other's data.
 */
const createBusinessJSONStorage = <S,>(getBaseStorage: () => StateStorage = () => localStorage) =>
  createJSONStorage<S>((): StateStorage => {
    return {
      getItem: (key) => {
        return getBaseStorage().getItem(getStorageKey(key))
      },
      setItem: (key, value) => {
        getBaseStorage().setItem(getStorageKey(key), value)
      },
      removeItem: (key) => {
        getBaseStorage().removeItem(getStorageKey(key))
      },
    }
  })

/**
 * Creates a business-aware persisted Zustand store that automatically handles business id switching.
 * The API interface is the same as `persist` from Zustand except for the `storage` parameter.
 * Instead of passing PersistStorage you can pass `getBaseStorage` e.g. localStorage, sessionStorage which is used to create a scoped PersistStorage storage.
 **/
const createPersistedBusinessStore = <T,>(
  initializer: Parameters<typeof persist<T>>[0],
  options: Omit<Parameters<typeof persist<T>>[1], 'storage'> & {
    getBaseStorage?: () => StateStorage
  },
) => {
  const storage = createBusinessJSONStorage(options.getBaseStorage)

  const store = create(
    persist(initializer, {
      ...options,
      storage,
    }),
  )
  const initialState = store.getState()

  businessStores.add({
    reInit: () => {
      if (storage?.getItem(options.name) === null) {
        // hack to reset the store to its initial state in case preserved value is null
        store.setState(initialState)
      }
      store.persist?.rehydrate?.()
    },
  })

  return store
}

export {
  initBusinessStorage,
  // createBusinessJSONStorage - Exported for testing purposes only
  createBusinessJSONStorage,
  createPersistedBusinessStore,
}
